import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from './auth.service';

export interface SubscriptionStatus {
  isActive: boolean;
  expiresAt?: string;
  message?: string;
}

export interface PaymentIntentResponse {
  success: boolean;
  paymentIntentId?: string;
  clientSecret?: string;
  message?: string;
}

export interface PaymentConfirmationResponse {
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private subscriptionStatusSubject = new BehaviorSubject<SubscriptionStatus | null>(null);
  public subscriptionStatus$ = this.subscriptionStatusSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Check for subscription errors on service initialization
    this.checkForStoredErrors();
  }

  /**
   * Check subscription status from the API
   */
  public checkSubscriptionStatus(): Observable<SubscriptionStatus> {
    return this.http.get<SubscriptionStatus>(
      `${environment.apiUrl}/api/tenant/subscription/status`
    );
  }

  /**
   * Check for stored subscription errors in localStorage
   */
  private checkForStoredErrors(): void {
    const errorData = localStorage.getItem('subscription_error');
    if (errorData) {
      try {
        const error = JSON.parse(errorData);
        // Only use errors that are less than 1 hour old
        const errorTime = new Date(error.timestamp).getTime();
        const currentTime = new Date().getTime();
        const hourInMs = 60 * 60 * 1000;

        if (currentTime - errorTime < hourInMs) {
          this.subscriptionStatusSubject.next({
            isActive: false,
            message: error.message
          });
        } else {
          // Clear old error
          localStorage.removeItem('subscription_error');
        }
      } catch (e) {
        // Invalid JSON, clear it
        localStorage.removeItem('subscription_error');
      }
    }
  }

  /**
   * Clear any subscription errors
   */
  public clearSubscriptionError(): void {
    localStorage.removeItem('subscription_error');
    this.subscriptionStatusSubject.next(null);
  }

  /**
   * Get the current subscription error message if any
   */
  public getSubscriptionErrorMessage(): string | null {
    const status = this.subscriptionStatusSubject.value;
    return status && !status.isActive ? status.message || 'Subscription issue detected' : null;
  }

  /**
   * Get the raw subscription status string from the auth service token
   */
  public getSubscriptionStatus(): string {
    return this.authService.getSubscriptionStatus();
  }

  /**
   * Handle subscription payment
   * @param amount The payment amount in ZAR
   * @returns Observable with payment intent details including client secret
   */
  public handleSubscriptionPayment(amount: number): Observable<PaymentIntentResponse> {
    return this.http.post<PaymentIntentResponse>(
      `${environment.apiUrl}/api/payment/create-payment-intent`,
      { amount, currency: 'zar' }
    ).pipe(
      catchError(error => {
        //console.error('Error creating payment intent:', error);
        return throwError(() => new Error(error.error?.message || 'Failed to process payment request'));
      })
    );
  }

  /**
   * Confirm subscription payment with the backend
   * @param paymentIntentId The Stripe payment intent ID to confirm
   * @returns Observable with confirmation result
   */
  public confirmSubscriptionPayment(paymentIntentId: string): Observable<PaymentConfirmationResponse> {
    return this.http.post<PaymentConfirmationResponse>(
      `${environment.apiUrl}/api/payment/confirm-payment`,
      { paymentIntentId }
    ).pipe(
      tap(response => {
        if (response.success) {
          // Clear any subscription errors on successful payment
          this.clearSubscriptionError();
        }
      }),
      catchError(error => {
        //console.error('Error confirming payment:', error);
        return throwError(() => new Error(error.error?.message || 'Failed to confirm payment'));
      })
    );
  }

  /**
   * Store the user's email for the payment flow
   * @param email The user's email address
   */
  public storeEmailForPayment(email: string): void {
    localStorage.setItem('rememberedEmail', email);
  }

  /**
   * Get the stored email for payment
   * @returns The stored email or empty string
   */
  public getStoredEmail(): string {
    return localStorage.getItem('rememberedEmail') || '';
  }
}
